/**
 * Captcha 驗證統一處理
 *
 * 根據區域自動選擇驗證方式:
 * - ACN: Cloudflare Turnstile
 * - GLOBAL: Google reCAPTCHA v3
 *
 * @param action - reCAPTCHA v3 的 action 參數,用於區分不同表單場景 (預設: 'submit')
 *
 * @returns {
 *   captcha: 驗證結果 (token 和 randomKey),
 *   verifyCaptcha: 接收 FormCaptcha 組件的驗證結果,
 *   expireCaptcha: 處理驗證過期,
 *   validateCaptcha: 統一驗證入口 (自動判斷區域並執行對應驗證),
 *   resetCaptcha: 統一重置方法 (自動處理兩種驗證的重置)
 * }
 */

interface ICaptcha {
  token: string;
  randomKey: string;
}

export const useCaptcha = (action: string = 'submit') => {
  const { isCN } = useLanguage();
  const captcha = ref<ICaptcha>({ token: '', randomKey: '' });
  const { $notification } = useNuxtApp();

  /**
   * ACN: 接收驗證結果
   */
  const verifyCaptcha = ({ token, randomKey }: ICaptcha): void => {
    captcha.value = { token: token || '', randomKey: randomKey || '' };
  };

  /**
   * ACN: 處理驗證過期
   */
  const expireCaptcha = (): void => {
    captcha.value = { token: '', randomKey: '' };
  };

  /**
   * GLOBAL: 執行 reCAPTCHA v3
   */
  const executeRecaptcha = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          const siteKey =
            document.documentElement.dataset.captchaClientKey || '';
          window.grecaptcha
            .execute(siteKey, { action })
            .then((token: string) => resolve(token))
            .catch((error: Error) => reject(error));
        });
      } else {
        reject(new Error('reCAPTCHA not loaded'));
      }
    });
  };

  /**
   * GLOBAL 與 ACN
   * 根據區域執行對應的驗證流程
   */
  const validateCaptcha = async (): Promise<boolean> => {
    if (isCN.value) {
      if (!captcha.value.token) {
        $notification({
          message: 'Please complete the verification.',
          type: 'warning',
        });
        return false;
      }
      return true;
    } else {
      try {
        const token = await executeRecaptcha();
        if (!token) {
          throw new Error('Failed to get reCAPTCHA token');
        }

        captcha.value.token = token;
        captcha.value.randomKey = '';
        return true;
      } catch (error) {
        console.error('reCAPTCHA error:', error);
        $notification({
          message:
            'reCAPTCHA verification failed. Please try again after 2 minutes.',
          type: 'warning',
        });
        return false;
      }
    }
  };

  /**
   * GLOBAL 與 ACN
   * 根據區域重置 captcha
   */
  const resetCaptcha = (): void => {
    if (isCN.value && window.turnstile) {
      window.turnstile.reset();
    }
    captcha.value = { token: '', randomKey: '' };
  };

  return {
    captcha,
    verifyCaptcha,
    expireCaptcha,
    resetCaptcha,
    validateCaptcha,
  };
};
